"use client";

import { useMemo } from "react";

import { type EventIndex, layoutWeekSegments } from "../event-layout";
import { type CalendarEvent } from "../types";
import { getDayKey } from "../utils";

import { EventBar } from "./event-bar";

// =============================================================================

type WeekAllDayRowProps = {
  days: Date[];
  eventIndex: EventIndex;
};

/**
 * All-day strip between the day headers and the hours, sticky below the
 * header. Absent when the week holds no all-day event.
 */
export function WeekAllDayRow({ days, eventIndex }: WeekAllDayRowProps) {
  const segments = useMemo(() => {
    const byId = new Map<string, CalendarEvent>();
    for (const day of days) {
      for (const event of eventIndex.allDayByDay.get(getDayKey(day)) ?? []) {
        byId.set(event.id, event);
      }
    }
    return layoutWeekSegments(days, [...byId.values()]);
  }, [days, eventIndex]);

  if (!segments.length) return null;

  return (
    <div className="sticky top-14 z-20 flex border-b border-line bg-background">
      <div className="sticky left-0 z-10 w-16 shrink-0 border-r border-line bg-background" />
      <div
        className="grid flex-1 auto-rows-6 py-1"
        style={{
          gridTemplateColumns: `repeat(${days.length}, minmax(0, 1fr))`,
        }}
      >
        {segments.map((segment) => (
          <EventBar key={segment.event.id} segment={segment} showTime={false} />
        ))}
      </div>
    </div>
  );
}
